// Round-half-up to N decimals (C option)
function roundScore(value, decimals = 2) {
    const factor = Math.pow(10, decimals);
    const rounded = Math.round(value * factor) / factor;
    // Avoid -0
    return Object.is(rounded, -0) ? 0 : rounded;
}

// Safe bool check (handles "true"/true)
function isTrue(val) {
    return val === true || val === "true";
}
export function updateDragMatchSelection(attempt, itemId, add) {
    attempt.dragMatches ??= [];

    if (add) {
        if (!attempt.dragMatches.includes(itemId))
            attempt.dragMatches.push(itemId);
    } else {
        attempt.dragMatches = attempt.dragMatches.filter(id => id !== itemId);
    }
}
export function getOrderIndex(optionTitle, dropdownValues) {
  if (!dropdownValues) return -1;
  try {
    const parsed = Array.isArray(dropdownValues)
      ? dropdownValues
      : JSON.parse(dropdownValues);

    return parsed.findIndex(item => item.optionTitle === optionTitle);
  } catch (err) {
    console.error("getOrderIndex parse error:", err);
    return -1;
  }
}

export function updateAttemptSelection(
    question,
    attempt,
    optionId = null,
    isChecked = null,
    userAnswer = null,
    dropdownValueId = null
) {
    const queType = question.queType;

    // Ensure storage structure exists
    attempt.selectedOptionIds ??= [];
    attempt.dropdownAnswers ??= {};

    // ======== MCQ / True-False ========
    if (queType === "MCQ" || queType === "True-False") {
        if (optionId) {
            attempt.selectedOptionIds = [optionId];
        } else {
            attempt.selectedOptionIds = [];
        }
        return attempt;
    }

    // ======== MSQ ========
    if (queType === "MSQ") {
        const option = question.options.find(o => o.ID == optionId);

        // Dropdown support
        if (option?.displayType === "dropdown") {
            // Ensure array for that option exists
            attempt.dropdownAnswers[optionId] ??= [];

            if (isChecked && dropdownValueId) {
                // Add only if not already included
                if (!attempt.dropdownAnswers[optionId].includes(dropdownValueId)) {
                    attempt.dropdownAnswers[optionId].push(dropdownValueId);
                }
            } else if (!isChecked && dropdownValueId) {
                // Remove if unselected
                attempt.dropdownAnswers[optionId] =
                    attempt.dropdownAnswers[optionId].filter(id => id !== dropdownValueId);

                // Clean up empty arrays to avoid noise
                if (attempt.dropdownAnswers[optionId].length === 0) {
                    delete attempt.dropdownAnswers[optionId];
                }
            }

        } else {
            // Normal MSQ checkbox
            const index = attempt.selectedOptionIds.indexOf(optionId);
            if (isChecked && index === -1) {
                attempt.selectedOptionIds.push(optionId);
            } else if (!isChecked && index !== -1) {
                attempt.selectedOptionIds.splice(index, 1);
            }
        }

        return attempt;
    }

    // ======== NAT ========
    if (queType === "NAT") {
        attempt.userAnswer = userAnswer;
        return attempt;
    }

    return attempt;
}

export function evaluateQuestion(question, attempt = {}) {
    const queType = question.queType;
    const totalMarks = parseFloat(question.marks) || 0;
    const negativeMarks = parseFloat(question.negativeMarks) || 0;

    const selectedOptionIds = new Set(attempt.selectedOptionIds || []);
    const userAnswer = attempt.userAnswer ?? null;

    let rawScore = 0;
    let status = "skipped"; // "correct" | "partially-correct" | "wrong" | "skipped"
    let isCorrect = false;

    // ---------- TRUE-FALSE / MCQ ----------
    if (queType === "True-False" || queType === "MCQ") {
        if (selectedOptionIds.size === 0) {
            // skipped
            rawScore = 0;
            status = "skipped";
        } else {
            const selectedId = [...selectedOptionIds][0];
            const opt = question.options.find(o => o.ID == selectedId);

            if (!opt) {
                // invalid selection: treat as wrong but no negative (or you can apply negative)
                rawScore = negativeMarks > 0 ? -negativeMarks : 0;
                status = "wrong";
            } else {
                const correct = isTrue(opt.isCorrect);
                if (correct) {
                    rawScore = totalMarks;
                    status = "correct";
                    isCorrect = true;
                } else {
                    rawScore = negativeMarks > 0 ? -negativeMarks : 0;
                    status = "wrong";
                }
            }
        }
    }

  

// ---------- MSQ (Multiple Select) ----------
else if (queType === "MSQ") {
    const dropdownAnswers = attempt.dropdownAnswers || {};

    const hasCheckboxSelections = selectedOptionIds.size > 0;
    const hasDropdownSelections = Object.keys(dropdownAnswers).length > 0;

    if (!hasCheckboxSelections && !hasDropdownSelections) {
        rawScore = 0;
        status = "skipped";
    } else {
        let wrongSelected = false;
        let correctScore = 0;

        const correctOptions = question.options.filter(o => {
            const dropdown = o.dropdownValues ? JSON.parse(o.dropdownValues) : [];
            const anyDropdownCorrect = dropdown.some(d => d.choiceCorrect === true);
            return isTrue(o.isCorrect) || anyDropdownCorrect;
        });

        const totalCorrectParentsCount = correctOptions.length;

        // ---- 1️⃣ Evaluate Dropdown MSQ first ----
        Object.entries(dropdownAnswers).forEach(([optionId, selectedDropIds]) => {
            const option = question.options.find(o => o.ID == optionId);
            if (!option?.dropdownValues) return;

            const dropdownItems = JSON.parse(option.dropdownValues || "[]");

            selectedDropIds.forEach(selectedDropId => {
                const item = dropdownItems.find(d => d.id == selectedDropId);
                if (!item) return;

                if (item.choiceCorrect) {
                    // partial or fallback to equal split
                    const partial = parseFloat(item.partialMarksChoice);
                    if (!isNaN(partial) && partial > 0) {
                        correctScore += partial;
                    } else {
                        correctScore += totalMarks / totalCorrectParentsCount;
                    }
                } else {
                    wrongSelected = true;
                }
            });
        });

        // ---- 2️⃣ Evaluate Checkbox MSQ ----
        selectedOptionIds.forEach(id => {
            const opt = question.options.find(o => o.ID == id);
            if (!opt) return;

            // skip dropdown parent itself — handled above
            if (opt.displayType === "dropdown") return;

            if (isTrue(opt.isCorrect)) {
                correctScore += totalMarks / totalCorrectParentsCount;
            } else {
                wrongSelected = true;
            }
        });

        // ---- Final Score & Status ----
        if (wrongSelected) {
            rawScore = negativeMarks > 0 ? -negativeMarks : 0;
            status = "wrong";
        } else if (correctScore >= totalMarks) {
            rawScore = totalMarks;
            status = "correct";
            isCorrect = true;
        } else if (correctScore > 0) {
            rawScore = correctScore;
            status = "partially-correct";
        } else {
            rawScore = 0;
            status = "wrong";
        }
    }

    rawScore = roundScore(rawScore, 2);
}
// ---------- Drag Matching ----------
else if (queType === "Drag Matching") {
    const dragMatches = attempt.dragMatches || [];

    if (dragMatches.length === 0) {
        rawScore = 0;
        status = "skipped";
    } else {
        let wrongSelected = false;
        let correctScore = 0;

        const option = question.options[0];
        const data = JSON.parse(option.dropdownValues || "[]");

        dragMatches.forEach(matchId => {
            const item = data.find(d => d.id == matchId);
            if (!item) return;

            if (item.choiceCorrect) {
                const partial = parseFloat(item.partialMarksChoice);
                if (!isNaN(partial) && partial > 0) {
                    correctScore += partial;
                } else {
                    // If no partial provided → fallback equal distribution
                    const correctItemsCount = data.filter(d => d.choiceCorrect === true).length;
                    correctScore += correctItemsCount > 0
                        ? totalMarks / correctItemsCount
                        : 0;
                }
            } else {
                wrongSelected = true;
            }
        });

        if (wrongSelected) {
            rawScore = negativeMarks > 0 ? -negativeMarks : 0;
            status = "wrong";
        } else if (correctScore >= totalMarks) {
            rawScore = totalMarks;
            status = "correct";
            isCorrect = true;
        } else if (correctScore > 0) {
            rawScore = correctScore;
            status = "partially-correct";
        } else {
            rawScore = 0;
            status = "wrong";
        }
    }

    rawScore = roundScore(rawScore, 2);
}


    // ---------- NAT (Numerical Answer Type) ----------
    else if (queType === "NAT") {
        // Treat empty answer as skipped, not wrong
        if (userAnswer === null || userAnswer === "") {
            rawScore = 0;
            status = "skipped";
        } else {
            const natOption = question.options[0]; // your schema uses first option as meta
            const userValue = parseFloat(userAnswer);

            if (isNaN(userValue)) {
                // invalid number -> wrong
                rawScore = negativeMarks > 0 ? -negativeMarks : 0;
                status = "wrong";
            } else {
                const isSingle = natOption.optionSingle === "1" || natOption.isSingleRange === "true";

                if (isSingle) {
                    // exact numeric match (from textBoxValue)
                    const correctValue = parseFloat(natOption.textBoxValue);
                    if (!isNaN(correctValue) && userValue === correctValue) {
                        rawScore = totalMarks;
                        status = "correct";
                        isCorrect = true;
                    } else {
                        rawScore = negativeMarks > 0 ? -negativeMarks : 0;
                        status = "wrong";
                    }
                } else {
                    // range-based answer
                    let range = { start: null, end: null };
                    try {
                        range = JSON.parse(natOption.optionRange || "{}");
                    } catch (e) {}

                    const start = parseFloat(range.start);
                    const end = parseFloat(range.end);

                    if (!isNaN(start) && !isNaN(end) && userValue >= start && userValue <= end) {
                        rawScore = totalMarks;
                        status = "correct";
                        isCorrect = true;
                    } else {
                        rawScore = negativeMarks > 0 ? -negativeMarks : 0;
                        status = "wrong";
                    }
                }
            }
        }
    }

    // ---------- UNKNOWN TYPE ----------
    else {
        rawScore = 0;
        status = "skipped";
    }

    const finalScore = roundScore(rawScore, 2);

    return {
        questionId: question.ID,
        queType,
        score: finalScore,       // rounded, for storing/showing
        rawScore,                // unrounded, if you want analytics
        status,                  // "correct" | "partially-correct" | "wrong" | "skipped"
        isCorrect,
        selectedOptionIds: [...selectedOptionIds],
        userAnswer
    };
}
